/**
 * entranceCodes32 — the 32-pada ENTRANCE READING lookup (static reference data).
 *
 * Mirror of the PKA canonical `entrance_codes_32pada.json` (SSOT lives in PKA
 * reference: PKA/Owner's Inbox/05_PKA_Reference/AV_VASTU_EFFECTS_DB/); kept
 * in-portal like the AUO palettes for a CLIENT-SIDE lookup. If PKA updates,
 * mirror here. Data copied VERBATIM — do not alter any effect/colour text.
 *
 * The entrance reading is a STATIC pada→effect table, NOT a computed engine, so
 * a door's reading is derived client-side (no callable, no deploy): for each pada
 * a door covers, resolve `{ effect, entrColour, zoneColour }`. A door spanning
 * N3–N4 yields TWO findings (N3 + N4) — one per pada — mirroring the report
 * composer's per-pada rule.
 *
 * 32 entries: N1–N8, E1–E8, S1–S8, W1–W8.
 */

export interface EntranceCode {
  /** the door strip's corrective/entrance colour NAME (White/Blue/Green/Red/Yellow). */
  entrColour: string;
  /** the pada zone's colour NAME. */
  zoneColour: string;
  /** the verbatim effect sentence for a door landing on this pada. */
  effect: string;
}

/** The canonical 32-pada entrance table (verbatim mirror of the PKA JSON). */
export const ENTRANCE_CODES_32PADA: Record<string, EntranceCode> = {
  N1: { entrColour: 'White', zoneColour: 'White', effect: "Harmed by others' bad intentions" },
  N2: { entrColour: 'White', zoneColour: 'Blue', effect: 'Fear of enemies, others jealous' },
  N3: { entrColour: 'White', zoneColour: 'Blue', effect: 'Money and more male progeny' },
  N4: { entrColour: 'White', zoneColour: 'Blue', effect: 'Inherited & earned money abundance' },
  N5: { entrColour: 'Green', zoneColour: 'Blue', effect: 'Religious, non-aggressive, calm' },
  N6: { entrColour: 'White', zoneColour: 'Blue', effect: 'Others avoid listening' },
  N7: { entrColour: 'Blue', zoneColour: 'Blue', effect: 'Grown girls go beyond tradition' },
  N8: { entrColour: 'Blue', zoneColour: 'Blue', effect: 'Higher bank balance' },
  E1: { entrColour: 'Green', zoneColour: 'Green', effect: 'Accidents without Recovery' },
  E2: { entrColour: 'Blue', zoneColour: 'Green', effect: 'More girls born, wasteful expenditure' },
  E3: { entrColour: 'Blue', zoneColour: 'Green', effect: 'Brings money, profits & success' },
  E4: { entrColour: 'Red', zoneColour: 'Green', effect: 'Closer to important people in government' },
  E5: { entrColour: 'Green', zoneColour: 'Green', effect: 'Short tempered, bordering insanity' },
  E6: { entrColour: 'Yellow', zoneColour: 'Green', effect: 'Causes fire, accidents, unexpected loss' },
  E7: { entrColour: 'Red', zoneColour: 'Green', effect: "Insensitive to others' problems" },
  E8: { entrColour: 'Red', zoneColour: 'Red', effect: 'Accidents, financial & unexpected loss' },
  S1: { entrColour: 'Red', zoneColour: 'Red', effect: 'Negative effect for boy child' },
  S2: { entrColour: 'Green', zoneColour: 'Red', effect: 'Tendency to work for others' },
  S3: { entrColour: 'Yellow', zoneColour: 'Red', effect: 'Immense prosperity' },
  S4: { entrColour: 'Yellow', zoneColour: 'Red', effect: 'High success, more sons' },
  S5: { entrColour: 'Yellow', zoneColour: 'Red', effect: 'Rarely free from debts; feel incapable' },
  S6: { entrColour: 'White', zoneColour: 'Yellow', effect: 'Door of abysmal poverty' },
  S7: { entrColour: 'Red', zoneColour: 'Yellow', effect: 'Profession/relationship wasted' },
  S8: { entrColour: 'Red', zoneColour: 'Yellow', effect: 'Disconnection, financial/relationship damage' },
  W1: { entrColour: 'White', zoneColour: 'Yellow', effect: 'Affects finances & lifespan' },
  W2: { entrColour: 'White', zoneColour: 'White', effect: 'Career instability, lack of clarity' },
  W3: { entrColour: 'Yellow', zoneColour: 'White', effect: 'Amazing growth & prosperity' },
  W4: { entrColour: 'White', zoneColour: 'White', effect: 'Good entrance' },
  W5: { entrColour: 'Yellow', zoneColour: 'White', effect: 'Perfectionist, overambitious' },
  W6: { entrColour: 'Yellow', zoneColour: 'White', effect: 'Mental depression prone' },
  W7: { entrColour: 'White', zoneColour: 'White', effect: 'Loss of happiness, drug/alcohol' },
  W8: { entrColour: 'Blue', zoneColour: 'White', effect: 'Unfair/unlawful means' },
};

/**
 * Resolve one pada code (e.g. "N3") to its entrance reading, or null when the
 * pada isn't in the table (never fabricate — the caller shows a calm "no reading").
 */
export function entranceReadingForPada(pada: string | null | undefined): EntranceCode | null {
  const key = String(pada ?? '').trim().toUpperCase();
  return ENTRANCE_CODES_32PADA[key] ?? null;
}
